import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { Subject } from 'rxjs';
import { Organizations, User } from '../model';
import { GoogleIdentityService } from './google-identity.service';
import { StorageService } from './storage.services';
import { safeMcpReturnUrl } from './mcp-login-return';

@Injectable({
  providedIn: 'root',
})
export class UserSettingsService {
  user?: User;
  private readonly userChangesSubject = new Subject<User | null>();
  readonly userChanges$ = this.userChangesSubject.asObservable();

  constructor(
    private readonly _storage: StorageService,
    private readonly router: Router,
    private readonly googleIdentity: GoogleIdentityService,
  ) {}

  /**
   *
   * @param user
   * @param storageReady usado para indicar que el usuario ya esta en el local storage,
   * por lo que no es necesario guardarlo nuevamente.
   */
  setUser(token, storageReady?) {
    if (!token) {
      this.user = null;
      this.userChangesSubject.next(null);
      return;
    }

    const tokenDecode = jwtDecode(token);
    const user: User = {
      id: tokenDecode['id'],
      email: tokenDecode['email'],
      firstName: tokenDecode['first_name'],
      lastName: tokenDecode['last_name'],
      userFullName: tokenDecode['user_full_name'],
      role: tokenDecode['role'],
      roleName: tokenDecode['role_name'],
      currentLoggedOrganization: tokenDecode['current_logged_organization'],
      organizations: tokenDecode['organizations'],
      loginDate: new Date().toString(),
      access: tokenDecode['access'],
      orgModules: tokenDecode['orgModules'],
    };
    this.user = user;
    this.userChangesSubject.next(user);

    this._storage.guardar_storage('User', user);
    localStorage.setItem('token', token);
  }

  /**
   * Se cerro sesion. Lo escuchan los servicios `providedIn:'root'` que guardan
   * estado del usuario —socket, softphone, latido de presencia— para soltarlo.
   *
   * Va por evento y no llamandolos desde aqui a proposito: este servicio es
   * compartido por todo el CRM y no tiene por que conocer al modulo de agentes.
   * Cada feature se apunta a lo suyo.
   */
  private readonly loggedOutSubject = new Subject<void>();
  readonly loggedOut$ = this.loggedOutSubject.asObservable();

  logOut(suppressGooglePrompt = true, returnUrl?: string) {
    // Solo una salida voluntaria debe impedir que Google sugiera volver a entrar.
    if (suppressGooglePrompt) {
      this.googleIdentity.prepareForLogout();
    } else {
      this.googleIdentity.release();
    }
    this.setUser(null);
    this._storage.deleteItemFromStorage('User');
    this._storage.deleteItemFromStorage('token');
    // Antes de navegar: quien escuche tiene que soltar el estado del usuario
    // que se va, no del que entre despues.
    this.loggedOutSubject.next();
    const destination = safeMcpReturnUrl(returnUrl);
    if (destination) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: destination } });
    } else {
      this.router.navigate(['/login']);
    }
  }

  getUser(): User | null {
    const user = localStorage.getItem('User');
    if (!user) {
      return null;
    }

    try {
      const userObject = JSON.parse(user);

      const userModel: User = {
        id: userObject['id'],
        email: userObject['email'],
        firstName: userObject['firstName'],
        lastName: userObject['lastName'],
        userFullName: userObject['userFullName'],
        role: userObject['role'],
        roleName: userObject['roleName'],
        currentLoggedOrganization: userObject['currentLoggedOrganization'],
        organizations: userObject['organizations'],
        access: userObject['access'],
        orgModules: userObject['orgModules'],
      };

      return userModel;
    } catch (error) {
      console.error('Invalid user data in storage', error);
      return null;
    }
  }

  loadUser() {
    this._storage
      .cargar_storage('User')
      .then(result => {
        // Cargamos el usaurio en memoria
        this.setUser(result, true);
      })
      .catch(err => {
        console.log({ err });
      });
  }

  setToken(token) {
    this.user.token = token;
  }

  getToken(): string {
    return localStorage.getItem('token') ?? '';
  }

  userLoaded(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.user && this.user.token) {
        resolve(true);
      } else {
        console.log('Loading user');
        this._storage
          .cargar_storage('User')
          .then(result => {
            if (result && result['token'] && result['loginDate'] && result['email']) {
              console.log('User loaded from storage');
              console.log(result);
              this.setUser(result, true);
              resolve(true);
            } else {
              this.logOut(false);
              resolve(false);
            }
            resolve(true);
          })
          .catch(err => {
            console.log({ err });

            resolve(false);
          });
      }
    });
  }

  validateToken() {
    const token = this.getToken();
    if (token) {
      const tokenValid = jwtDecode(token);
      const now = Math.floor(Date.now() / 1000);

      return tokenValid.exp > now;
    }
  }

  rememberUserEmail(email: string) {
    localStorage.setItem('loginEmail', email);
  }
  removeUserEmail() {
    localStorage.removeItem('loginEmail');
  }
  getUserEmail() {
    return localStorage.getItem('loginEmail');
  }
  setUserOrganizations(organizations: Organizations[]) {
    if (organizations.length > 0) {
      this._storage.guardar_storage('User', { ...this.user, organizations });
    }
  }

  updateOrganizationSnapshot(organization: Partial<Organizations>): User | null {
    const currentUser = this.getUser();
    if (!currentUser?.currentLoggedOrganization || organization?.id == null) return currentUser;

    const updatedUser: User = {
      ...currentUser,
      currentLoggedOrganization: {
        ...currentUser.currentLoggedOrganization,
        ...organization,
      },
      organizations: (currentUser.organizations || []).map(current =>
        current.id === organization.id ? { ...current, ...organization } : current,
      ),
    };

    this.user = updatedUser;
    this._storage.guardar_storage('User', updatedUser);
    this.userChangesSubject.next(updatedUser);
    return updatedUser;
  }
}
